import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { dataCollectionParser } from '../lib/response/dataParser';

function getState() {
  return {
    id: '',
    name: '',
    audience: {
      includedLists: []
    },
    audienceCount: 0,
    audienceTotalExclusionCount: 0,
    creditsUsed: 0,
    previewHtml: '',
    sms: {
      text: '',
      html: '',
      fields: '',
      shortenUrls: null,
      urlFields: 0
    },
    updatedAt: 0,
    scheduledSendAt: 0,
    bulkSendAnalytics: {},
    dataCaptureAnalytics: {},
    engagementLinks: [],
    uniqueEngagementLinks: [],
    engagementShortenedLinks: [],
    classicTemplateId: null,
    unsubscribedContacts: [],
    campaignsAverageDeliveredPercentage: 0,
    campaignsAverageUniqueClickthroughPercentage: 0,
    messages: [],
    messagesCount: 0,
    autoResumeAt: null
  };
}

export const state = getState();

export const getters = {
  campaignLinksExpireAt(state): number {
    if (!state.scheduledSendAt) return 0;
    const daysCampaignLinksValid = 125;
    const dayInSeconds = 86400;

    return state.scheduledSendAt + daysCampaignLinksValid * dayInSeconds;
  }
};

export const mutations = {
  setCampaignAnalytics(state: any, snapshot): void {
    state.id = snapshot.id;
    state.name = snapshot.name;
    state.audience = snapshot.audience;
    state.audienceCount = snapshot.audienceCount;
    state.creditsUsed = snapshot.creditsUsed;
    state.sms = snapshot.sms;
    state.updatedAt = snapshot.updatedAt;
    state.scheduledSendAt = snapshot.scheduledSendAt;
    state.previewHtml = snapshot.previewHtml;
    state.classicTemplateId = snapshot.classicTemplateId;
    state.audienceTotalExclusionCount = snapshot.audienceTotalExclusionCount;
    state.autoResumeAt = snapshot.autoResumeAt;
  },

  setBulkSendAnalytics(state: any, { bulkSendAnalytics }): void {
    state.bulkSendAnalytics = bulkSendAnalytics;
  },

  setDataCaptureAnalytics(state: any, { dataCaptureAnalytics }): void {
    state.dataCaptureAnalytics = dataCaptureAnalytics;
  },

  setEngagementLinks(state: any, { engagementLinks }): void {
    state.engagementLinks = engagementLinks;
  },

  setUniqueEngagementLinks(state: any, { uniqueEngagementLinks }): void {
    state.uniqueEngagementLinks = uniqueEngagementLinks;
  },

  setEngagementShortenedLinks(state: any, { engagementShortenedLinks }): void {
    state.engagementShortenedLinks = engagementShortenedLinks;
  },

  setUnsubscribedContacts(state: any, { unsubscribedContacts }): void {
    state.unsubscribedContacts = unsubscribedContacts;
  },

  setState(state: any, newState: any) {
    Object.keys(state).forEach(key => {
      state[key] = newState[key];
    });
  },

  setMessages(
    state: any,
    { messages }: { messages: Record<string, any>[] }
  ): void {
    state.messages = messages;
  },

  setMessagesCount(state: any, { messagesCount }): void {
    state.messagesCount = messagesCount;
  }
};

export const actions = {
  async loadCampaignAnalytics(
    { commit },
    { id, fields, period }
  ): Promise<ActionResponse> {
    commit('setState', getState());

    try {
      const response = await createAxios().get(
        '/backend/campaign-analytics-show',
        {
          params: {
            campaignId: id,
            'fields[campaigns-analytics]': fields
              ? fields
              : 'name,step,updatedAt,scheduledSendAt,autoResumeAt,sms,audienceCount,audienceTotalExclusionCount,creditsUsed,audience,bulkSendAnalytics,previewHtml,classicTemplateId,dataCaptureAnalytics',
            ...(!!period && { period })
          }
        }
      );

      commit('setCampaignAnalytics', {
        id: response.data.data.id,
        ...response.data.data.attributes
      });
      commit('setBulkSendAnalytics', {
        bulkSendAnalytics: response.data.data.attributes.bulkSendAnalytics
      });
      commit('setDataCaptureAnalytics', {
        dataCaptureAnalytics: response.data.data.attributes.dataCaptureAnalytics
      });
      commit('setEngagementLinks', {
        engagementLinks: response.data.data.attributes.engagementLinks
      });
      commit('setUniqueEngagementLinks', {
        uniqueEngagementLinks:
          response.data.data.attributes.uniqueEngagementLinks
      });
      commit('setEngagementShortenedLinks', {
        engagementShortenedLinks:
          response.data.data.attributes.engagementShortenedLinks
      });
      commit('setUnsubscribedContacts', {
        unsubscribedContacts: response.data.data.attributes.unsubscribedContacts
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadCampaignAnalyticsSummary(
    context,
    { campaignId }: { campaignId: string }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/campaign-analytics-show',
        {
          params: {
            campaignId,
            'fields[campaigns-analytics]': 'bulkSendAnalyticsSummary'
          }
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadMessages(
    { commit },
    {
      campaignId,
      limit,
      offset,
      fromTimestamp
    }: {
      campaignId: string | string[];
      limit: number;
      offset: number;
      fromTimestamp: number | null;
    }
  ): Promise<ActionResponse> {
    const campaignFilter = Array.isArray(campaignId)
      ? {
          'filter[campaignId][val]': `IN (${campaignId
            .map(x => `"${x}"`)
            .join(',')})`,
          'filter[campaignId][op]': 'raw'
        }
      : {
          'filter[campaignId][val]': campaignId,
          'filter[campaignId][op]': 'eq'
        };
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).get(
        '/data-api/sent-messages-index',
        {
          params: {
            'page[limit]': limit,
            'page[offset]': offset,
            ...campaignFilter,
            ...(fromTimestamp && {
              'filter[updatedAt][val]': fromTimestamp,
              'filter[updatedAt][op]': 'gt'
            })
          }
        }
      );

      const { meta } = response.data;
      commit('setMessages', { messages: dataCollectionParser(response) });

      // Use this if response is in standard JSON API format:
      commit('setMessagesCount', { messagesCount: meta.totalRecords });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async downloadCampaignUnsubscribes(
    { commit, rootState },
    campaignId: string
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/campaign-opted-out-contacts-export-create',
        {
          data: {
            type: 'campaignOptedOutContactsExport',
            attributes: {
              campaignId: campaignId
            }
          }
        }
      );
      return { success: true, errors: {}, body: response };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
